import type { ProjectDetailsStoryboardShot, ProjectDetailsVisual } from "@/types/project";
import { STORYBOARD_VIDEO_MODEL_USER_FILE_UPLOAD } from "@/utils/constants";
import { moveImageIdsToFront, prependImageIdIfMissing } from "@/utils/utils";

function isUserUploadVisualForPrimaryVideo(v: ProjectDetailsVisual, primaryVideoId: string): boolean {
  return (
    v.status === "completed" &&
    typeof v.video_id === "string" &&
    v.video_id !== "" &&
    v.video_id === primaryVideoId &&
    v.model === STORYBOARD_VIDEO_MODEL_USER_FILE_UPLOAD
  );
}

function completedUserUploadVideoId(shot: ProjectDetailsStoryboardShot): string | null {
  const v = (shot.visuals ?? []).find(
    (x) => x.status === "completed" && x.model === STORYBOARD_VIDEO_MODEL_USER_FILE_UPLOAD && typeof x.video_id === "string" && x.video_id !== ""
  );
  return v?.video_id ?? null;
}

/** Replace shot `images` (does not touch `approved-timeline-images`). */
export function patchStoryboardShotImagesForMainSlotChange(shot: ProjectDetailsStoryboardShot, newImages: string[]): ProjectDetailsStoryboardShot {
  return { ...shot, images: newImages };
}

/** Frame/image Run complete: new asset at `images[0]` via {@link moveImageIdsToFront}. */
export function storyboardShotWithGeneratedImageAtFront(shot: ProjectDetailsStoryboardShot, newImageId: string): ProjectDetailsStoryboardShot {
  return patchStoryboardShotImagesForMainSlotChange(shot, moveImageIdsToFront(shot.images, [newImageId]));
}

/** Reorders `images` to bring `[imageId, videoId]` to the front for MainSpace display — does NOT touch `approved-timeline-images`. */
export function patchStoryboardShotDisplayedImage(
  shot: ProjectDetailsStoryboardShot,
  imageId: string,
  videoId?: string | null
): ProjectDetailsStoryboardShot {
  let nextImages = prependImageIdIfMissing(shot.images, imageId);
  const vTrim = typeof videoId === "string" ? videoId.trim() : "";
  if (vTrim.length > 0) {
    const hasVisual = (shot.visuals ?? []).some(
      (v) =>
        v.status === "completed" &&
        v.reference_image_id === imageId &&
        typeof v.video_id === "string" &&
        v.video_id === vTrim
    );
    if (hasVisual) {
      nextImages = moveImageIdsToFront(nextImages, [imageId, vTrim]);
    }
  }

  return patchStoryboardShotImagesForMainSlotChange(shot, nextImages);
}

/**
 * Approve the dropped/displayed asset: a video id approves the **video** and leaves the approved frame
 * alone, no video id approves the still. The two approvals are independent (see the approval model in
 * `VIDEO_MAINSPACE_BEHAVIOR_SPEC.md`).
 */
export function patchStoryboardShotApprovedTimelineImage(
  shot: ProjectDetailsStoryboardShot,
  imageId: string,
  videoId?: string | null
): ProjectDetailsStoryboardShot {
  const vTrim = typeof videoId === "string" ? videoId.trim() : "";
  /** A video can be approved with no still to show for it (legacy `[video]`-only `images`). */
  const displayed = imageId === "" ? shot : patchStoryboardShotDisplayedImage(shot, imageId, videoId);
  if (vTrim.length > 0) {
    return { ...displayed, "approved-video-id": vTrim };
  }

  if (imageId === "") {
    return displayed;
  }

  return storyboardShotWithApprovedFrame(displayed, imageId, shot);
}

/**
 * Approve a still. When the shot already has a video, the frame also replaces the shot's own frames
 * as the lead Video-panel reference, so the next run is generated from it.
 */
export function storyboardShotWithApprovedFrame(
  shot: ProjectDetailsStoryboardShot,
  imageId: string,
  videoSourceShot: ProjectDetailsStoryboardShot = shot
): ProjectDetailsStoryboardShot {
  const approved = { ...shot, "approved-timeline-images": [imageId] };
  if (storyboardShotMainVideoId(videoSourceShot) === null) {
    return approved;
  }

  const shotStills = new Set(shot.images);
  const kept = (shot["video-uploaded-images"] ?? []).filter((id) => id !== imageId && !shotStills.has(id));
  return { ...approved, "video-uploaded-images": [imageId, ...kept] };
}

/** Still pinned first in the Video panel for first-frame models: the approved frame once it leads the references. */
export function storyboardVideoPanelPrimaryStillImageId(shot: ProjectDetailsStoryboardShot | undefined): string | null {
  const approved = shot?.["approved-timeline-images"]?.[0]?.trim() ?? "";
  if (approved !== "" && shot?.["video-uploaded-images"]?.[0] === approved) {
    return approved;
  }

  return storyboardWorkingStillImageId(shot);
}

/**
 * Double-click timeline: promote the slot's approved asset to the front of `images`. An approved video
 * promotes as `[its poster, the video]`; otherwise the approved still alone.
 */
export function patchStoryboardShotPromoteApprovedTimelineToImagesFront(
  shot: ProjectDetailsStoryboardShot,
  videoId?: string | null
): ProjectDetailsStoryboardShot | null {
  const approvedFrame = shot["approved-timeline-images"]?.[0];
  const frameId = typeof approvedFrame === "string" ? approvedFrame : "";
  const vTrim = typeof videoId === "string" ? videoId.trim() : "";
  const hasCompletedVideo = vTrim !== "" && (shot.visuals ?? []).some((v) => v.status === "completed" && v.video_id === vTrim);
  if (!hasCompletedVideo && frameId === "") {
    return null;
  }

  const stillId = hasCompletedVideo ? approvedStoryboardShotVideoPosterImageId(shot, vTrim) ?? frameId : frameId;
  const nextImages = moveImageIdsToFront(shot.images, hasCompletedVideo ? [stillId, vTrim] : [stillId]);

  const prevImages = shot.images ?? [];
  if (nextImages.length === prevImages.length && nextImages.every((id, index) => id === prevImages[index])) {
    return null;
  }

  return patchStoryboardShotImagesForMainSlotChange(shot, nextImages);
}

/**
 * `video_id` from this shot’s `visuals` for a `reference_image_id` match, **only** when `status === "completed"`.
 * MainSpace / timeline must not treat in-progress or failed rows as playable output.
 */
export function getCompletedVideoIdForReferenceImage(
  shot: ProjectDetailsStoryboardShot | undefined,
  referenceImageId: string | null | undefined,
  preferredVideoId?: string | null
): string | null {
  if (shot === undefined || referenceImageId === null || referenceImageId === undefined || referenceImageId === "") {
    return null;
  }

  function visualMatchesReference(v: ProjectDetailsVisual): boolean {
    return v["reference_image_id"] === referenceImageId && typeof v["video_id"] === "string" && v["video_id"] !== "";
  }

  const matching = (shot.visuals ?? []).filter((v) => visualMatchesReference(v));
  const preferred =
    typeof preferredVideoId === "string" && preferredVideoId !== ""
      ? matching.find((v) => v.status === "completed" && v["video_id"] === preferredVideoId)
      : undefined;
  const completed = preferred ?? matching.find((v) => v.status === "completed");
  const byRef = completed?.["video_id"] ?? null;
  if (byRef !== null && byRef !== undefined && byRef !== "") {
    return byRef;
  }

  /** Legacy user-upload row: `images[0]` was the video id and matched `reference_image_id`. */
  const primary0 = shot.images?.[0];
  if (typeof primary0 === "string" && primary0 !== "" && primary0 === referenceImageId) {
    const userUpload = (shot.visuals ?? []).find((v) => isUserUploadVisualForPrimaryVideo(v, primary0));
    if (userUpload !== undefined && typeof userUpload.video_id === "string" && userUpload.video_id !== "") {
      return userUpload.video_id;
    }
  }

  return null;
}

/**
 * When `images` is ordered `[reference_image_id, video_id, …]` for a completed AI visual, prefer that
 * `video_id` when resolving the playable output for {@link getCompletedVideoIdForReferenceImage}.
 */
export function preferredVideoIdFromStoryboardShotImages(shot: ProjectDetailsStoryboardShot | null | undefined): string | null {
  if (shot === undefined || shot === null) {
    return null;
  }

  const ref0 = shot.images?.[0];
  const id1 = shot.images?.[1];
  if (typeof ref0 !== "string" || ref0 === "" || typeof id1 !== "string" || id1 === "") {
    return null;
  }

  const v = (shot.visuals ?? []).find(
    (x) => x.status === "completed" && x.reference_image_id === ref0 && x.video_id === id1
  );
  return typeof v?.video_id === "string" && v.video_id !== "" ? v.video_id : null;
}

/** `images[0]` is a completed output file (`visuals[].video_id`). */
function shotImagesPrimaryIsCompletedVideoId(shot: ProjectDetailsStoryboardShot, id: string): boolean {
  return (shot.visuals ?? []).some((v) => v.status === "completed" && typeof v.video_id === "string" && v.video_id === id && id !== "");
}

/** Shot includes a completed user-upload MP4 (`file_upload` visual) whose `video_id` is listed on the shot. */
export function isStoryboardShotPrimarySlotUploadedVideo(shot: ProjectDetailsStoryboardShot | undefined): boolean {
  if (shot === undefined) {
    return false;
  }

  const vid = completedUserUploadVideoId(shot);
  return vid !== null && (shot.images ?? []).includes(vid);
}

/** Still id for `/image` thumbs: poster/ref first when `[poster, video]`; else legacy `[video, poster]` uses `images[1]`; else `images[0]`. */
export function storyboardWorkingStillImageId(shot: ProjectDetailsStoryboardShot | undefined): string | null {
  if (shot === undefined) {
    return null;
  }

  const w = shot.images?.[0];
  if (typeof w !== "string" || w === "") {
    return null;
  }

  const uploadVid = completedUserUploadVideoId(shot);
  if (uploadVid !== null) {
    const imgs = shot.images ?? [];
    if (imgs[0] !== undefined && imgs[0] !== "" && imgs[0] !== uploadVid) {
      return imgs[0];
    }

    const second = imgs[1];
    return typeof second === "string" && second !== "" ? second : null;
  }

  if (shotImagesPrimaryIsCompletedVideoId(shot, w)) {
    const second = shot.images?.[1];
    return typeof second === "string" && second !== "" ? second : null;
  }

  return w;
}

/** Still for the Frame tab: never a displayed video's reference frame when the shot has an approved one. */
export function storyboardFrameStillImageId(shot: ProjectDetailsStoryboardShot | undefined): string | null {
  const working = storyboardWorkingStillImageId(shot);
  if (preferredVideoIdFromStoryboardShotImages(shot) === null) {
    return working;
  }

  const approved = shot?.["approved-timeline-images"]?.[0];
  return typeof approved === "string" && approved.trim() !== "" ? approved.trim() : working;
}

/**
 * The user's stored video approval, or null when the shot has none — including shots saved before
 * `approved-video-id` existed, and stale ids that no longer name a completed visual. This is what
 * "is this video approved" must be answered with; never the legacy derivation, or a shot carrying an
 * approval this app never wrote would refuse to approve anything.
 */
export function storedApprovedStoryboardShotVideoId(shot: ProjectDetailsStoryboardShot | undefined): string | null {
  const stored = shot?.["approved-video-id"];
  if (typeof stored !== "string") {
    return null;
  }

  const trimmed = stored.trim();
  if (trimmed === "") {
    return null;
  }

  return (shot?.visuals ?? []).some((v) => v.status === "completed" && v.video_id === trimmed) ? trimmed : null;
}

/**
 * Video the timeline slot renders for a shot: the stored approval, or — only for shots saved before the
 * field existed — the legacy derivation (approved still → a completed visual generated from it), so those
 * shots keep looking exactly as they do today until someone approves on them.
 */
export function approvedStoryboardShotVideoId(shot: ProjectDetailsStoryboardShot | undefined): string | null {
  if (shot === undefined) {
    return null;
  }

  if (typeof shot["approved-video-id"] === "string") {
    return storedApprovedStoryboardShotVideoId(shot);
  }

  /**
   * No preferred-video argument on purpose: the legacy derivation must not follow what MainSpace happens to
   * display, or browsing the library would keep moving a shot's play badge around.
   */
  return getCompletedVideoIdForReferenceImage(shot, shot["approved-timeline-images"]?.[0]);
}

/** Still shown for an approved video in the timeline strip. */
export function approvedStoryboardShotVideoPosterImageId(shot: ProjectDetailsStoryboardShot, videoId: string): string | null {
  const visual = (shot.visuals ?? []).find((v) => v.status === "completed" && v.video_id === videoId);
  if (visual === undefined) {
    return null;
  }

  const poster = visual.poster_image_id?.trim() ?? "";
  if (poster !== "") {
    return poster;
  }

  const derived = posterImageIdForCompletedVideoVisual(shot, visual);
  return derived === "" ? null : derived;
}

/**
 * MainSpace video on the Video tab: the shot's most-recently-displayed video (via `images[0]`/`images[1]`
 * pairing — the latest generation, drag-and-drop, or upload), regardless of approval. Approval only
 * governs the Timeline strip ({@link buildStoryboardTimelineItems}), never MainSpace directly.
 * Falls back to the approved timeline still's completed visual when there is no "preferred" video
 * (e.g. the shot has never had anything displayed beyond an approve action). Shots with neither show
 * the working still ({@link storyboardWorkingStillImageId}) or empty.
 */
export function storyboardShotMainVideoId(shot: ProjectDetailsStoryboardShot | undefined): string | null {
  if (shot === undefined) {
    return null;
  }

  const preferred = preferredVideoIdFromStoryboardShotImages(shot);
  if (preferred !== null) {
    const hasCompleted = (shot.visuals ?? []).some((v) => v.status === "completed" && v.video_id === preferred);
    if (hasCompleted) {
      return preferred;
    }
  }

  return approvedStoryboardShotVideoId(shot);
}

/** @deprecated Use {@link storyboardShotMainVideoId} for MainSpace playback. */
export function storyboardShotPlaybackReferenceImageId(shot: ProjectDetailsStoryboardShot | undefined): string {
  if (shot === undefined) {
    return "";
  }

  const approved0 = shot["approved-timeline-images"]?.[0];
  if (typeof approved0 === "string" && approved0 !== "") {
    return approved0;
  }

  return primaryReferenceImageIdForStoryboardShotVideo(shot);
}

/** Prefer a specific completed video only when not using an approved-timeline reference. */
export function preferredVideoIdForStoryboardShotPlayback(shot: ProjectDetailsStoryboardShot | undefined): string | null {
  if (shot === undefined) {
    return null;
  }

  const approved0 = shot["approved-timeline-images"]?.[0];
  if (typeof approved0 === "string" && approved0 !== "") {
    return null;
  }

  return preferredVideoIdFromStoryboardShotImages(shot);
}

/** Ref id for matching `visuals`: if primary is video asset, use visual `reference_image_id`; else `images[0]` / approved timeline. */
export function primaryReferenceImageIdForStoryboardShotVideo(shot: ProjectDetailsStoryboardShot | undefined): string {
  if (shot === undefined) {
    return "";
  }

  const working0 = shot.images?.[0];
  if (typeof working0 === "string" && working0 !== "") {
    if (shotImagesPrimaryIsCompletedVideoId(shot, working0)) {
      const visual = (shot.visuals ?? []).find((v) => v.status === "completed" && v.video_id === working0);
      const ref = visual?.reference_image_id;
      if (typeof ref === "string" && ref !== "") {
        return ref;
      }
    }

    return working0;
  }

  const approved0 = shot["approved-timeline-images"]?.[0];
  if (typeof approved0 === "string" && approved0 !== "") {
    return approved0;
  }

  return "";
}

/** Poster/thumbnail for a completed video visual: own `poster_image_id`/`reference_image_id` first, else a shot image not already claimed by another visual (never one two visuals would share). */
export function posterImageIdForCompletedVideoVisual(
  shot: ProjectDetailsStoryboardShot,
  visual: ProjectDetailsVisual
): string {
  const videoIds = new Set<string>();
  for (const row of shot.visuals ?? []) {
    const rowVideoId = row.video_id?.trim() ?? "";
    if (rowVideoId !== "") {
      videoIds.add(rowVideoId);
    }
  }

  const ownPosterId = visual.poster_image_id?.trim() ?? "";
  if (ownPosterId !== "" && !videoIds.has(ownPosterId)) {
    return ownPosterId;
  }

  const ownRefId = visual.reference_image_id?.trim() ?? "";
  if (ownRefId !== "" && !videoIds.has(ownRefId)) {
    return ownRefId;
  }

  const claimedByOtherVisuals = new Set<string>();
  for (const row of shot.visuals ?? []) {
    if (row.video_id === visual.video_id) {
      continue;
    }

    const rowPosterId = row.poster_image_id?.trim() ?? "";
    if (rowPosterId !== "" && !videoIds.has(rowPosterId)) {
      claimedByOtherVisuals.add(rowPosterId);
    }

    const rowRefId = row.reference_image_id?.trim() ?? "";
    if (rowRefId !== "" && !videoIds.has(rowRefId)) {
      claimedByOtherVisuals.add(rowRefId);
    }
  }

  for (const id of shot.images ?? []) {
    const trimmed = typeof id === "string" ? id.trim() : "";
    if (trimmed !== "" && !videoIds.has(trimmed) && !claimedByOtherVisuals.has(trimmed)) {
      return trimmed;
    }
  }

  return "";
}
